import React, { useRef, useState, useEffect } from "react";
import styles from "./SelectedList.module.css";
import { sanitizeInput, maxLenght } from "../Helper";
import Input from "../Forms/Input";
import { v4 as uuidv4 } from "uuid";
import { MdDelete } from "react-icons/md";

interface iSelectedList {
    setList: any;
    list: any;
    placeholder?: string;
    isType?: boolean;
    field: string;
    value?: any;
    readOnly?: boolean;
    className?: any;
    classNameDiv?: any;
    options?: string[];
    onBlur?: any;
    onKeyUp?: any;
    hasValue?: any;
    singleValue?: boolean;
}

const SelectedList: React.FC<iSelectedList> = ({
                                                   setList,
                                                   list = {},
                                                   placeholder,
                                                   isType,
                                                   field,
                                                   value,
                                                   readOnly,
                                                   className,
                                                   classNameDiv,
                                                   options,
                                                   onKeyUp,
                                                   hasValue,
                                                   ...props
                                               }) => {
    const [showOptions, setShowOptions] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>("");
    const optionsListRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (props.singleValue && field === "post_code") {
            setInputValue(list[field] || "");
        } else if (value !== undefined) {
            setInputValue(value || "");
        }
    }, [list[field], value, props.singleValue, field]);

    const handleAddItem = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (props.singleValue) {
            return;
        }
        let currentValue = e.currentTarget.value.trim();
        currentValue = sanitizeInput(currentValue);
        if ((e.key === "Enter" && currentValue !== "" && currentValue !== "")) {
            if (currentValue.length > maxLenght) {
                return;
              }
            if (field === "status" && list[field]?.length >= 1) {
                return;
            }
            if (field === "type" && list[field]?.length >= 1) {
                return;
            }
            if (field === "origin" && list[field]?.length >= 1) {
                return;
            }
            setList((prev: any) => ({
                ...prev,
                [field]: [...(prev[field] || []), currentValue],
            }));
            setInputValue(""); 
            e.preventDefault();
        }
    };

    const removeItem = (keyword: string) => {
        setList((prev: any) => {
            const updatedKeywords = Array.isArray(prev[field]) ? [...prev[field]] : [];
            const index = updatedKeywords.indexOf(keyword);
            if (index !== -1) {
                updatedKeywords.splice(index, 1);
            }
            return {
                ...prev,
                [field]: updatedKeywords,
            };
        });
    };

    const handleOption = (e: any) => {
        const option = e.currentTarget.value;
        if (field === "status" || field === "type" || field === "origin") {
            setList((prev: any) => ({
                ...prev,
                [field]: [option],
            }));
        } else {
            if (!list[field]) {
                setList((prev: any) => ({
                    ...prev,
                    [field]: [option],
                }));
            } else if (!list[field].includes(option)) {
                setList((prevRange: any) => ({
                    ...prevRange,
                    [field]: [...prevRange[field], option],
                }));
            }
        }
        setShowOptions(false);
    };

    const handleBlur = (e: React.FocusEvent) => {
        if (!optionsListRef.current?.contains(e.relatedTarget)) {
            setTimeout(() => {
                setShowOptions(false);
            }, 100);
        }

        if (props.onBlur) {
            props.onBlur(e);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newValue = e.target.value;

        if (props.singleValue && field === "post_code") {
            newValue = newValue.replace(/\D/g, '');
            
            setInputValue(newValue);
            setList((prev: any) => ({
                ...prev,
                [field]: newValue,
            }));
        } else if (!props.singleValue && newValue.includes(" ")) {
            const wordsArray = newValue.trim().split(" ");
            const lastWord = wordsArray[wordsArray.length - 1];

            if (lastWord !== "") {
                setList((prev: any) => ({
                    ...prev,
                    [field]: [...(prev[field] || []), lastWord],
                }));
            }
            setInputValue("");
        } else {
            setInputValue(newValue);
        }

        newValue !== "" ? hasValue(true) : hasValue(false);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        let pastedText = e.clipboardData.getData("text");

        if (props.singleValue && field === "post_code") {
            pastedText = pastedText.replace(/\D/g, '');
            setInputValue(pastedText);
            setList((prev: any) => ({
                ...prev,
                [field]: pastedText,
            }));
        } else {
            const wordsArray = pastedText.split(/[\s,.;:?()!]+/).filter(Boolean);
            setList((prev: any) => ({
                ...prev,
                [field]: [...(prev[field] || []), ...wordsArray],
            }));
            setInputValue(""); 
        }
    };

    return (
        <div className={styles.listContainer}>
            <Input
                className={`${styles.input} ${className}`}
                onKeyPress={handleAddItem}
                name={field}
                onPaste={handlePaste}
                placeholder={placeholder}
                onFocus={isType && (() => {
                    setShowOptions(true);
                })}
                onBlur={isType && handleBlur}
                value={inputValue}
                readOnly={readOnly}
                onKeyUp={onKeyUp}
                max={field ==="words" ? maxLenght : undefined}
                onChange={handleChange}
                {...props}
            />
            {showOptions && (
                <div className={styles.list} ref={optionsListRef}>
                    {options?.map((option: any) => (
                        <button
                            className={`${styles.option} ${list[field]?.includes(option) ? styles.selectedOption : ""}`}
                            key={uuidv4()}
                            value={option}
                            onClick={handleOption}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            )}
            {!props.singleValue && !showOptions && list[field]?.length > 0 && (
                <div className={styles.selectionContainer}>
                    <div className={styles.scrollWrapper}>
                        <div className={styles.selected}>
                            {list[field]?.map((item: string) => (
                                <div key={uuidv4()} className={`${styles.item} ${classNameDiv}`}>
                                    {item}
                                    <button
                                        className={styles.remove}
                                        onClick={() => removeItem(item)}
                                    >
                                        <MdDelete size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelectedList;